import {
  Badge,
  Box,
  Divider,
  HStack,
  VStack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { useLazyQuery, gql } from '@apollo/client';

import { CurrencyDisplay } from '@/components/Currency';
import { usePriceToDinero } from '@/utilities/priceToDinero';
import { useCart } from '@/state/cart/useCart';

import { CartItemTypeEnum } from '../TypeDefs';

// GraphQL query to get event details
const GET_EVENT_DETAILS = gql`
  query GetEventDetailsForOrderSummary($eventId: String!) {
    event(input: { _id: $eventId }) {
      eventV2 {
        _id
        name
      }
    }
  }
`;

export interface OrderSummaryListProps {
  // Display options
  showDetailedBreakdown?: boolean;
  maxItems?: number;
  // Styling
  itemSpacing?: number;
  fontSize?: string;
  // Layout
  maxHeight?: string | number;
  isScrollable?: boolean;
}

export const OrderSummaryList: React.FC<OrderSummaryListProps> = ({
  showDetailedBreakdown = false,
  maxItems,
  itemSpacing = 3,
  fontSize = 'sm',
  maxHeight,
  isScrollable = true,
}) => {
  const { items } = useCart();
  const priceToDinero = usePriceToDinero();
  const [eventNames, setEventNames] = React.useState<Record<string, string>>({});
  
  const [getEventDetails] = useLazyQuery(GET_EVENT_DETAILS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  // Fetch event names for all event items
  React.useEffect(() => {
    const fetchEventNames = async () => {
      if (!items) return;
      
      const eventItems = items.filter(item => 
        (item.type === CartItemTypeEnum.EVENTS ||
          item.type === CartItemTypeEnum.COURSE) &&
        item.eventId
      );
      
      const names: Record<string, string> = {};
      
      for (const item of eventItems) {
        if (item.eventId && !eventNames[item.eventId]) {
          try {
            const { data } = await getEventDetails({
              variables: { eventId: item.eventId }
            });
            
            const fetchedEventName = data?.event?.eventV2?.name;
            names[item.eventId] = fetchedEventName || 'Unknown Event';
          } catch (error) {
            console.error('Error fetching event details:', error);
            names[item.eventId] = 'Unknown Event';
          }
        }
      }
      
      if (Object.keys(names).length > 0) {
        setEventNames(prev => ({ ...prev, ...names }));
      }
    };

    fetchEventNames();
  }, [items, getEventDetails, eventNames]);

  if (!items || items.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Text fontSize={fontSize} color="gray.500">
          No items in cart
        </Text>
      </Box>
    );
  }

  const containerProps = {
    overflowY: isScrollable ? ('auto' as const) : undefined,
    maxHeight: maxHeight,
    minHeight: 0,
    height: maxHeight ? maxHeight : undefined,
  };

  return (
    <Box {...containerProps}>
      {showDetailedBreakdown ? (
        <VStack spacing={itemSpacing} align="stretch">
          {items.slice(0, maxItems).map((item) => {
            const unitPriceDinero = priceToDinero(item.unitPrice ?? 0);
            const subTotalDinero = priceToDinero(item.subTotal);
            const totalDinero = priceToDinero(item.total);
            const discountAmountDinero = priceToDinero(
              item.discountAmount * item.quantity
            );
            const hasDiscount = item.discountAmount > 0;

            return (
              <Box
                key={item._id}
                p={{ base: 2, md: 3 }}
                bg="white"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                shadow="sm"
              >
                {/* Item Name and Type */}
                <Box mb={2}>
                  <Text 
                    fontWeight="semibold" 
                    fontSize={fontSize}
                    mb={1}
                    noOfLines={2}
                  >
                    {item.name}
                  </Text>
                  {(item.type === CartItemTypeEnum.EVENTS ||
                    item.type === CartItemTypeEnum.COURSE) &&
                    item.eventId &&
                    eventNames[item.eventId] && (
                    <Text 
                      fontSize="xs"
                      color="gray.500" 
                      mb={1}
                      noOfLines={1}
                    >
                      Event: {eventNames[item.eventId]}
                    </Text>
                  )}
                  <Badge
                    size="sm"
                    colorScheme={
                      item.type === CartItemTypeEnum.EVENTS
                        ? 'blue'
                        : item.type === CartItemTypeEnum.COURSE
                        ? 'cyan'
                        : item.type === CartItemTypeEnum.COLLECTION
                        ? 'teal'
                        : 'purple'
                    }
                    variant="subtle"
                    fontSize="xs"
                  >
                    {item.type === CartItemTypeEnum.EVENTS
                      ? 'Event Ticket'
                      : item.type === CartItemTypeEnum.COURSE
                      ? 'Course'
                      : item.type === CartItemTypeEnum.MEMBERSHIP
                      ? 'Membership'
                      : item.type === CartItemTypeEnum.COLLECTION
                      ? 'Collection'
                      : 'Other Item'}
                  </Badge>
                </Box>

                <Divider my={2} />

                {/* Pricing details */}
                <VStack spacing={1} align="stretch">
                  <HStack justifyContent="space-between">
                    <Text fontSize="xs" color="gray.500">
                      Qty: {item.quantity}
                    </Text>
                    <CurrencyDisplay
                      value={unitPriceDinero}
                      fontSize="xs"
                      fontWeight="medium"
                    />
                  </HStack>

                  <HStack justifyContent="space-between">
                    <Text fontSize="xs" color="gray.500">
                      Subtotal:
                    </Text>
                    <CurrencyDisplay
                      value={subTotalDinero}
                      fontSize="xs"
                      fontWeight="medium"
                    />
                  </HStack>

                  {hasDiscount && (
                    <HStack justifyContent="space-between">
                      <Text fontSize="xs" color="gray.500">
                        Discount:
                      </Text>
                      <CurrencyDisplay
                        value={discountAmountDinero}
                        fontSize="xs"
                        fontWeight="medium"
                        color="error.500"
                      />
                    </HStack>
                  )}

                  <Divider my={1} />

                  {/* Item Total */}
                  <HStack justifyContent="space-between">
                    <Text fontSize="sm" fontWeight="semibold">
                      Item Total:
                    </Text>
                    <CurrencyDisplay
                      value={totalDinero}
                      fontSize="sm"
                      fontWeight="bold"
                      color="primary.500"
                    />
                  </HStack>
                </VStack>
              </Box>
            );
          })}
          {maxItems && items.length > maxItems && (
            <Text fontSize="xs" color="gray.500" textAlign="center">
              +{items.length - maxItems} more items
            </Text>
          )}
        </VStack>
      ) : (
        <VStack spacing={itemSpacing} align="stretch">
          {items.slice(0, maxItems).map((item) => (
            <Box key={item._id}>
              <HStack justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} minW={0}>
                  <Text 
                    fontSize={fontSize}
                    fontWeight="medium"
                    noOfLines={2}
                    mb={1}
                  >
                    {item.name}
                  </Text>
                  {(item.type === CartItemTypeEnum.EVENTS ||
                    item.type === CartItemTypeEnum.COURSE) &&
                    item.eventId &&
                    eventNames[item.eventId] && (
                    <Text 
                      fontSize="xs"
                      color="gray.500" 
                      noOfLines={1}
                      mb={1}
                    >
                      Event: {eventNames[item.eventId]}
                    </Text>
                  )}
                </Box>
                {/* Inline quantity and price display */}
                <HStack spacing={1} flexShrink={0}>
                  <Text fontSize={fontSize} whiteSpace="nowrap">
                    {item.quantity}
                  </Text>
                  <Text fontSize={fontSize} whiteSpace="nowrap">
                    x
                  </Text>
                  <CurrencyDisplay
                    value={priceToDinero(item.unitPrice ?? 0)}
                    fontSize={fontSize}
                    fontWeight="semibold"
                    whiteSpace="nowrap"
                  />
                </HStack>
              </HStack>
            </Box>
          ))}
          {maxItems && items.length > maxItems && (
            <Text fontSize="xs" color="gray.500" textAlign="center">
              +{items.length - maxItems} more items
            </Text>
          )}
        </VStack>
      )}
    </Box>
  );
};